import React from 'react';
import clsx from 'clsx';
import styles from './ScreenPrimitives.module.scss';
import { colors, severity, coverDesign, coverFonts, ptToPx } from '../data/designTokens';
import type { Severity } from '../data/designTokens';
import { splitOrgName } from '../data/coverHelpers';
import { ScreenCoverNotch } from './CoverNotch';
import { ScreenPoweredByAica } from './PoweredByAicaSvg';

const DISPLAY_FONT = coverFonts.screen.display;

export const ScreenPage: React.FC<{
    children?: React.ReactNode;
    orgName?: string;
    sectionLabel?: string;
    pageNumber?: number;
    totalPages?: number;
    showHeader?: boolean;
}> = ({ children, orgName, sectionLabel, pageNumber, totalPages, showHeader = true }) => (
    <div className={styles.ScreenPage}>
        {showHeader && (
            <div className={styles.RunningHeader}>
                <span>{orgName ?? ''}</span>
                <span>{sectionLabel ?? 'AICA Credit Report'}</span>
            </div>
        )}
        {children}
        <div className={styles.RunningFooter}>
            <span>AICA Report &mdash; Confidential</span>
            {pageNumber != null && totalPages != null ? (
                <span>
                    {String(pageNumber).padStart(2, '0')} / {String(totalPages).padStart(2, '0')}
                </span>
            ) : null}
        </div>
    </div>
);

export const ScreenCover: React.FC<{
    orgName: string;
    subtitle?: string;
    meta: Array<{ label: string; value: string }>;
    generatedOn: string;
    pnoLogoUrl?: string;
}> = ({ orgName, meta, generatedOn, pnoLogoUrl }) => {
    const { main, suffix } = splitOrgName(orgName);
    const c = coverDesign;
    const f = coverFonts.screen;
    return (
        <div
            data-report-cover=""
            style={{
                background: colors.cover,
                color: colors.coverInk,
                borderRadius: 4,
                paddingTop: ptToPx(c.paddingTop),
                paddingBottom: ptToPx(c.paddingBottom),
                paddingLeft: ptToPx(c.paddingX),
                paddingRight: ptToPx(c.paddingX),
                margin: '0 auto 28px',
                width: ptToPx(c.pageWidth),
                maxWidth: '100%',
                minHeight: ptToPx(c.pageHeight),
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: f.sans,
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            <ScreenCoverNotch pnoLogoUrl={pnoLogoUrl} />
            {/* Powered-by wordmark stays in its original top-left position,
                white-on-cover; only the PNO logo moved into the notch plate.
                Inline SVG (not the hosted <img>) so the server-export Lambda
                always paints it without a network fetch. */}
            <div style={{ display: 'flex', alignItems: 'center', minHeight: ptToPx(c.pnoLogoMaxHeight) }}>
                <ScreenPoweredByAica width={ptToPx(c.poweredByLogoWidth)} height={ptToPx(c.poweredByLogoHeight)} fill={colors.coverInk} />
            </div>

            <div style={{ marginTop: ptToPx(c.topToTitle) }}>
                <div
                    style={{
                        fontSize: ptToPx(c.titleSize),
                        lineHeight: c.titleLineHeight,
                        letterSpacing: `${c.titleTrackingEm}em`,
                        color: colors.coverInk,
                        fontFamily: f.display,
                        fontWeight: 400,
                    }}
                >
                    {main}
                </div>
                {suffix ? (
                    <div
                        style={{
                            fontSize: ptToPx(c.suffixSize),
                            lineHeight: c.suffixLineHeight,
                            letterSpacing: `${c.suffixTrackingEm}em`,
                            color: colors.coverInkSoft,
                            fontFamily: f.display,
                            fontWeight: 400,
                            marginTop: ptToPx(c.suffixMarginTop),
                        }}
                    >
                        {suffix}
                    </div>
                ) : null}
            </div>

            <div
                style={{
                    width: ptToPx(c.ruleWidth),
                    height: ptToPx(c.ruleHeight),
                    background: colors.coverRule,
                    marginTop: ptToPx(c.ruleMarginTop),
                    marginBottom: ptToPx(c.ruleMarginBottom),
                }}
            />

            <div>
                {meta.map((m) => (
                    <div key={m.label} style={{ display: 'flex', alignItems: 'center', minHeight: ptToPx(c.metaRowHeight) }}>
                        <span
                            style={{
                                width: ptToPx(c.metaLabelWidth),
                                fontSize: ptToPx(c.metaLabelSize),
                                fontFamily: f.mono,
                                color: colors.coverInkMuted,
                                letterSpacing: `${c.metaLabelTrackingEm}em`,
                                textTransform: 'uppercase',
                            }}
                        >
                            {m.label}
                        </span>
                        <span
                            style={{
                                fontSize: ptToPx(c.metaValueSize),
                                fontFamily: f.mono,
                                color: colors.coverInkSoft,
                                marginLeft: ptToPx(c.metaLabelValueGap),
                            }}
                        >
                            {m.value}
                        </span>
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 'auto' }}>
                <div
                    style={{
                        fontSize: ptToPx(c.bottomLabelSize),
                        fontFamily: f.sans,
                        fontWeight: 500,
                        color: colors.coverInk,
                        letterSpacing: `${c.bottomLabelTrackingEm}em`,
                        textTransform: 'uppercase',
                    }}
                >
                    Company Due Diligence Report
                </div>
                <div
                    style={{
                        marginTop: ptToPx(c.bottomGap),
                        fontSize: ptToPx(c.bottomDateSize),
                        fontFamily: f.mono,
                        color: colors.coverInkSoft,
                        letterSpacing: `${c.bottomDateTrackingEm}em`,
                    }}
                >
                    Generated · {generatedOn}
                </div>
            </div>
        </div>
    );
};

export const ScreenSection: React.FC<{
    kicker?: string;
    title: string;
    subtitle?: string;
    index?: string;
    children: React.ReactNode;
}> = ({ kicker, title, subtitle, index, children }) => {
    const eyebrow = [index, kicker].filter(Boolean).join(' · ');
    return (
        <section className={styles.ScreenSection}>
            <div className={styles.SectionHeader}>
                {eyebrow ? (
                    <div className={styles.SectionKicker} style={{ ['--section-accent' as any]: colors.accent }}>
                        {eyebrow}
                    </div>
                ) : null}
                <div className={styles.SectionTitle} style={{ fontFamily: DISPLAY_FONT }}>
                    {title}
                </div>
                {subtitle ? <div className={styles.SectionSubtitle}>{subtitle}</div> : null}
            </div>
            <div className={styles.SectionBody}>{children}</div>
        </section>
    );
};

export const ScreenH3: React.FC<{ children: React.ReactNode; subtitle?: string; topGap?: 0 | 16 }> = ({
    children,
    subtitle,
    topGap = 16,
}) => (
    <div className={clsx(styles.H3, { [styles.H3Flush]: topGap === 0 })}>
        <div className={styles.H3Title}>{children}</div>
        {subtitle ? <div className={styles.H3Subtitle}>{subtitle}</div> : null}
    </div>
);

export const ScreenParagraph: React.FC<{ children: React.ReactNode }> = ({ children }) => <p className={styles.Paragraph}>{children}</p>;

export const ScreenKvList: React.FC<{
    items: Array<{ label: string; value?: string | null }>;
}> = ({ items }) => (
    <div>
        {items
            .filter((x) => x.value != null && x.value !== '')
            .map((x) => (
                <div key={x.label} className={styles.KvRow}>
                    <div className={styles.KvLabel}>{x.label}</div>
                    <div className={styles.KvValue}>{x.value}</div>
                </div>
            ))}
    </div>
);

export const ScreenKpiCard: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
    <div className={styles.KpiCard}>
        <div className={styles.KpiLabel}>{label}</div>
        <div className={styles.KpiValue}>{value}</div>
        {sub ? <div className={styles.KpiSub}>{sub}</div> : null}
    </div>
);

export const ScreenKpiGrid: React.FC<{ items: Array<{ label: string; value: string; sub?: string }> }> = ({ items }) => (
    <div className={styles.KpiGrid}>
        {items.map((x) => (
            <ScreenKpiCard key={x.label} {...x} />
        ))}
    </div>
);

export const ScreenChip: React.FC<{ tone: Severity | 'info' | 'brand' | 'muted' | 'accent'; children: React.ReactNode }> = ({
    tone,
    children,
}) => {
    let bg: string = colors.neutralSoft;
    let fg: string = colors.ink40;
    if (tone === 'info') {
        bg = colors.infoSoft;
        fg = colors.info;
    } else if (tone === 'brand' || tone === 'accent') {
        bg = colors.accentSoft;
        fg = colors.accentDeep;
    } else if (tone === 'muted') {
        bg = colors.neutralSoft;
        fg = colors.ink40;
    } else {
        bg = severity[tone].bg;
        fg = severity[tone].fg;
    }
    return (
        <span className={styles.Chip} style={{ backgroundColor: bg, color: fg }}>
            {children}
        </span>
    );
};

type Column = {
    key: string;
    header: string;
    width?: number | string;
    align?: 'left' | 'right' | 'center';
};

export const ScreenDataTable: React.FC<{
    columns: Column[];
    rows: Array<Record<string, React.ReactNode>>;
    zebra?: boolean;
}> = ({ columns, rows, zebra = true }) => {
    const colStyle = (c: Column): React.CSSProperties => {
        const base: React.CSSProperties = { padding: '0 4px', textAlign: c.align ?? 'left' };
        if (c.width != null) {
            (base as any).width = c.width;
        } else {
            base.flex = 1;
        }
        return base;
    };
    return (
        <div className={styles.Table}>
            <div className={styles.HeadRow}>
                {columns.map((c) => (
                    <div key={c.key} className={styles.HeadCell} style={colStyle(c)}>
                        {c.header}
                    </div>
                ))}
            </div>
            {rows.map((row, i) => (
                <div key={i} className={`${styles.Row} ${zebra && i % 2 === 1 ? styles.Alt : ''}`}>
                    {columns.map((c) => (
                        <div key={c.key} className={styles.Cell} style={colStyle(c)}>
                            {row[c.key] as any}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

export const ScreenDivider: React.FC = () => <div className={styles.Divider} />;

export const ScreenMonthGrid: React.FC<{
    title: string;
    primary?: string;
    secondary?: string;
    cells: Array<{ label: string; short: string; yearShort: string; status: 'on_time' | 'delayed' | 'default' | 'missing' }>;
}> = ({ title, primary, secondary, cells }) => {
    const klass = (s: string) =>
        s === 'on_time' ? styles.OnTime : s === 'delayed' ? styles.Delayed : s === 'default' ? styles.Default : styles.Missing;
    return (
        <div className={styles.MonthGrid}>
            <div className={styles.Head}>
                <span className={styles.HeadTitle}>{title}</span>
                {primary ? (
                    <span className={styles.HeadMeta}>
                        <span className={styles.HeadPrimary}>{primary}</span>
                        {secondary ? ` ${secondary}` : ''}
                    </span>
                ) : null}
            </div>
            <div className={styles.Row}>
                {cells.map((c) => (
                    <div key={c.label} className={`${styles.Cell} ${klass(c.status)}`}>
                        <span>{c.short}</span>
                        <span className={styles.Year}>'{c.yearShort}</span>
                    </div>
                ))}
            </div>
            {/* Legend dots removed — see PdfPrimitives PdfMonthGrid for the
                shared rationale. Canonical legend now lives in ScreenStatusBar. */}
        </div>
    );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
    <span className={styles.Item}>
        <span className={styles.Dot} style={{ backgroundColor: color }} />
        <span>{label}</span>
    </span>
);

export const ScreenSparkBars: React.FC<{ values: number[]; height?: number }> = ({ values, height = 40 }) => {
    const safe = values.map((v) => (Number.isFinite(v) ? v : 0));
    const max = Math.max(1, ...safe);
    return (
        <div className={styles.SparkBar} style={{ height }}>
            {safe.map((v, i) => (
                <div key={i} className={styles.Col} style={{ height: `${Math.max(2, (v / max) * height)}px` }} />
            ))}
        </div>
    );
};

type StatusSegment = { label: string; count: number; color: string };
const SCREEN_LEGEND_TONE: Record<string, { bg: string; fg: string; glyph: string }> = {
    'On time': { bg: '#D1FAE5', fg: '#059669', glyph: '✓' },
    Delayed: { bg: '#FEF3C7', fg: '#D97706', glyph: '!' },
    Default: { bg: '#FEE2E2', fg: '#DC2626', glyph: '✕' },
    Missing: { bg: '#F3F4F6', fg: '#9CA3AF', glyph: '—' },
};
export const ScreenStatusBar: React.FC<{ segments: StatusSegment[] }> = ({ segments }) => {
    const total = segments.reduce((s, seg) => s + seg.count, 0);
    if (total <= 0) return null;
    return (
        <div>
            <div style={{ display: 'flex', height: 8, borderRadius: 2, overflow: 'hidden' }}>
                {segments.map((seg, i) => (seg.count > 0 ? <div key={i} style={{ flex: seg.count, background: seg.color }} /> : null))}
            </div>
            {/* Single-row legend — see PdfStatusBar in PdfPrimitives for the
                shared rationale. The percentage bar carries the counts. */}
            <div style={{ display: 'flex', gap: 14, marginTop: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                {segments.map((seg, i) => {
                    if (seg.count <= 0) return null;
                    const tone = SCREEN_LEGEND_TONE[seg.label] ?? { bg: seg.color, fg: '#FFFFFF', glyph: '·' };
                    return (
                        <div key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                            <span
                                style={{
                                    width: 14,
                                    height: 14,
                                    background: tone.bg,
                                    color: tone.fg,
                                    borderRadius: 3,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 9,
                                    fontWeight: 700,
                                    lineHeight: 1,
                                }}
                            >
                                {tone.glyph}
                            </span>
                            <span
                                style={{
                                    fontSize: 10.5,
                                    color: colors.ink40,
                                    letterSpacing: '0.05em',
                                    textTransform: 'uppercase',
                                    fontWeight: 600,
                                    lineHeight: 1,
                                }}
                            >
                                {seg.label}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const sampleScreenAxisIndices = (n: number, cap = 6): number[] => {
    if (n <= 0) return [];
    if (n <= cap) return Array.from({ length: n }, (_, i) => i);
    const out = new Set<number>([0, n - 1]);
    const step = (n - 1) / (cap - 1);
    for (let k = 1; k < cap - 1; k++) out.add(Math.round(k * step));
    return Array.from(out).sort((a, b) => a - b);
};

export const ScreenDelayBars: React.FC<{
    cells: Array<{ short: string; yearShort: string; delayDays?: number; status: string }>;
}> = ({ cells }) => {
    // `?? 0` doesn't catch NaN / Infinity — those propagate through max and
    // poison every point's y-coord (NaN SVG attributes render blank). Use
    // Number.isFinite() and clamp negatives. Skill rule 25.
    const safeDelay = (v: unknown): number => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0 ? n : 0;
    };
    const max = Math.max(1, ...cells.map((c) => safeDelay(c.delayDays)));
    if (max <= 1) return null;
    const yMid = Math.round(max / 2);
    const labelIdx = new Set(sampleScreenAxisIndices(cells.length, cells.length <= 18 ? cells.length : 12));

    // SVG viewBox geometry — labels live inside the same SVG (skill rule 11),
    // so x-axis text shares the dot x-coordinate exactly.
    const width = 600;
    const padTop = 12;
    const padBottom = 16;
    const padLeft = 28;
    const padRight = 8;
    const chartH = 64;
    const height = padTop + chartH + padBottom;
    const chartW = width - padLeft - padRight;
    const xStep = chartW / Math.max(1, cells.length - 1);
    const points = cells.map((c, i) => {
        const v = safeDelay(c.delayDays);
        return {
            x: padLeft + i * xStep,
            y: padTop + chartH - (v / max) * chartH,
            v,
            status: c.status,
            short: c.short,
            yearShort: c.yearShort,
        };
    });
    const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    const dotColor = (status: string, v: number) => (status === 'default' ? colors.neg : v > 0 ? colors.warn : colors.neutralSoft);

    return (
        <div style={{ marginTop: 14 }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 6,
                }}
            >
                <span
                    style={{
                        fontSize: 10,
                        color: colors.ink40,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                    }}
                >
                    Delay days (per month)
                </span>
                <span
                    style={{
                        fontSize: 10,
                        color: colors.ink20,
                        fontFamily: "'Geist Mono', monospace",
                    }}
                >
                    Max {max}d
                </span>
            </div>
            <svg width="100%" viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
                {/* y-axis ticks: max / mid / 0 — gridlines + mono labels in left gutter */}
                {[
                    { val: max, y: padTop },
                    { val: yMid, y: padTop + chartH / 2 },
                    { val: 0, y: padTop + chartH },
                ].map((t, i) => (
                    <g key={`y-${i}`}>
                        <line x1={padLeft} y1={t.y} x2={width - padRight} y2={t.y} stroke={colors.hairlineSoft} strokeWidth={0.5} />
                        <text
                            x={padLeft - 4}
                            y={t.y + 3}
                            fill={colors.ink40}
                            fontSize={8}
                            fontFamily="'Geist Mono', monospace"
                            textAnchor="end"
                        >
                            {t.val}
                        </text>
                    </g>
                ))}
                {/* connected line in dominant warn tone — per-point status surfaced via dot color */}
                <path d={linePath} stroke={colors.warn} strokeWidth={1.2} fill="none" />
                {points.map((p, i) => (
                    <circle key={`d-${i}`} cx={p.x} cy={p.y} r={2.2} fill={dotColor(p.status, p.v)} />
                ))}
                {/* value labels above each dot (skip zeros to reduce noise) */}
                {points.map((p, i) =>
                    p.v > 0 ? (
                        <text
                            key={`v-${i}`}
                            x={p.x}
                            y={p.y - 4}
                            fill={colors.ink40}
                            fontSize={8}
                            fontFamily="'Geist Mono', monospace"
                            textAnchor="middle"
                        >
                            {p.v}
                        </text>
                    ) : null,
                )}
                {/* x-axis month labels — same x-coords as the dots (skill rule 11) */}
                {points.map((p, i) =>
                    labelIdx.has(i) ? (
                        <text
                            key={`x-${i}`}
                            x={p.x}
                            y={padTop + chartH + 11}
                            fill={colors.ink40}
                            fontSize={8}
                            fontFamily="'Geist Mono', monospace"
                            textAnchor="middle"
                        >
                            {p.short} &apos;{p.yearShort}
                        </text>
                    ) : null,
                )}
            </svg>
        </div>
    );
};
